import { gql } from '@apollo/client';
import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Modal, { ModalTitle } from '../../components/Modal';
import type { DeactivateConfirmationModal_UserFragment } from '../../graphql/generated/graphqlTypes';
import EntitySelect from '../../search/entitySearch/EntitySelect';
import UserRow from '../../user/UserRow';
import styles from './DeactivateConfirmationModal.module.scss';

const DEACTIVATE_CONFIRMATION_MODAL_USER = gql`
  fragment DeactivateConfirmationModal_user on User {
    id
    name
  }
`;

interface DeactivateConfirmationModalProps {
  close: () => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onConfirm: (newOwner: any) => Promise<void>;
  user: DeactivateConfirmationModal_UserFragment;
}

const SEARCH_CONFIG = { user: {} };

const DeactivateConfirmationModal = ({
  close,
  onConfirm,
  user: userToDeactivate,
}: DeactivateConfirmationModalProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const [isEditing, setIsEditing] = useState(false);
  const [newOwner, setNewOwner] =
    useState<React.ComponentProps<typeof UserRow>['user']>(currentUser);

  return (
    <Modal width={425}>
      <ModalTitle close={close}>Deactivate user?</ModalTitle>
      Ownership of {userToDeactivate.name}&apos;s published posts must be
      transferred to another user.
      <h5 className={styles.ownerHeading}>New owner</h5>
      <div className={styles.owner}>
        {isEditing ? (
          <EntitySelect
            autoFocus
            isClearable={false}
            onChange={(value) => {
              if (value?.type === 'user' && value.id !== userToDeactivate.id) {
                setNewOwner(value.user);
              }
              setIsEditing(false);
            }}
            value={{ id: currentUser.id, type: 'user' }}
            config={SEARCH_CONFIG}
          />
        ) : (
          <>
            <div className={styles.ownerCard}>
              <UserRow user={newOwner} />
            </div>
            <Button
              size="small"
              variant="secondary"
              onClick={() => setIsEditing(true)}
            >
              Change
            </Button>
          </>
        )}
      </div>
      <div className={styles.buttons}>
        <Button onClick={close} size="large" variant="secondary">
          Never mind
        </Button>
        <Button
          onClick={() => {
            onConfirm(newOwner);
            close();
          }}
          size="large"
          variant="primary"
        >
          Confirm
        </Button>
      </div>
    </Modal>
  );
};

DeactivateConfirmationModal.fragments = {
  user: DEACTIVATE_CONFIRMATION_MODAL_USER,
};

export default DeactivateConfirmationModal;
